import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { BillingTermDialogComponent, BillingTermDialogOutput } from '@galaxy/inventory-ui';
import { App } from '@galaxy/marketplace-apps';
import { FeatureFlagService, FeatureFlagStatusInterface } from '@galaxy/partner';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { Package } from '@vendasta/marketplace-packages';
import {
  BillingPeriod,
  DurationInterface,
  LineItem,
  Origin,
  Revenue,
  RevenueComponent,
  RevenuePeriod,
  SalesOrdersService,
  Status,
} from '@vendasta/sales-orders';
import { TaxOption } from '@vendasta/store';
import { BehaviorSubject, Observable, Subscription, combineLatest, iif } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { SalesOrderConfirmationService, UILineItemWithEditionPricing } from './sales-order-confirmation.service';
import { SalesOrderEditPriceComponent } from './sales-order-edit-price/sales-order-edit-price.component';
import { SalesOrderLineItemSelectorDialogComponent } from './sales-order-line-item-selector-dialog/sales-order-line-item-selector-dialog.component';

const ITEM_SELECTOR_INCLUDES_PACKAGES = '2023_11_ssc_item_selector_packages';
const SALES_OPPORTUNITIES_BILLING_PERIODS = '2024_01_sales_opportunities_billing_periods';

@Component({
  selector: 'sales-ui-sales-order-confirmation',
  templateUrl: './sales-order-confirmation.component.html',
  styleUrls: ['../../shared/shared-styles.scss', './sales-order-confirmation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [SalesOrderConfirmationService],
})
export class SalesOrderConfirmationComponent implements OnInit, OnDestroy {
  @Input() partnerId: string;
  @Input() marketId: string;
  @Input() businessId: string;

  @Input() orderId: string;
  @Input() opportunityId: string;
  @Input() lineItems: LineItem[];
  @Input() hidePackages = false;
  @Input() removeHeader: boolean;
  @Input() title: string;
  @Input() editable = false;
  @Input() startEditing = false;
  @Input() defaultDisplayCurrency: string;
  @Input() allowNegativeValues = false;
  // allows multiple quantities to be set for products that are not multi-activatable
  @Input() bypassQuantityRestrictions = false;
  // controls whether or not an order can be directly charged, allowing for PC origin orders to display pricing.
  @Input() chargeOrdersEnabled = false;
  @Input() enableEditBillingSchedule = false;
  @Input() allowDuplicateItems = false;
  @Input() createRetailSubscriptions = false;

  @Output() loading: EventEmitter<boolean> = new EventEmitter();

  openExpansionPanel$$ = new BehaviorSubject<boolean>(false);
  openExpansionPanel$ = this.openExpansionPanel$$.asObservable();
  formForLineItems: UntypedFormArray;
  editing = false;
  saving = false;
  displayItems: UILineItemWithEditionPricing[];
  editingItems: UILineItemWithEditionPricing[];
  uiLineItems$: Observable<UILineItemWithEditionPricing[]> = this.salesOrderConfirmationService.uiLineItems$;
  taxOptions$: Observable<TaxOption[]> = this.salesOrderConfirmationService.taxOptions$;
  loading$: Observable<boolean> = this.salesOrderConfirmationService.loading$;
  showPricing$: Observable<boolean> = this.salesOrderConfirmationService.order$.pipe(
    map(
      (order) =>
        order?.origin !== Origin.PC ||
        (this.chargeOrdersEnabled &&
          order?.statusHistory?.some((history) => history.status === Status.SUBMITTED_FOR_CUSTOMER_APPROVAL)),
    ),
  );
  inputDuration$$ = new BehaviorSubject<DurationInterface>(null);
  duration$ = this.inputDuration$$
    .asObservable()
    .pipe(
      switchMap((inputDuration) =>
        iif(
          () => !!inputDuration,
          this.inputDuration$$.asObservable(),
          this.salesOrderConfirmationService.contractDuration$,
        ),
      ),
    );
  estimatedTotal$ = this.salesOrderConfirmationService.estimatedTotal$;
  orderState$ = combineLatest([
    this.uiLineItems$,
    this.taxOptions$,
    this.duration$,
    this.loading$,
    this.estimatedTotal$,
  ]).pipe(
    map(([orderItems, taxOptions, duration, loading, estimatedTotal]) => ({
      orderItems: orderItems,
      taxOptions: taxOptions,
      duration: duration,
      loading: loading,
      estimatedTotal: estimatedTotal,
    })),
  );
  hideSellingStandaloneProducts = false;
  @ViewChildren('editPriceComponent')
  private readonly editPriceComponents: QueryList<SalesOrderEditPriceComponent>;
  private subscriptions: Subscription[] = [];

  featureFlags$: Observable<FeatureFlagStatusInterface>;
  areBillingPeriodsEnabled$: Observable<boolean>;

  constructor(
    private readonly salesOrderConfirmationService: SalesOrderConfirmationService,
    private fb: UntypedFormBuilder,
    private cdk: ChangeDetectorRef,
    private alertService: SnackbarService,
    private dialog: MatDialog,
    private readonly salesOrdersService: SalesOrdersService,
    private readonly featureFlagService: FeatureFlagService,
  ) {}

  @Input()
  set duration(duration: DurationInterface) {
    this.inputDuration$$.next(duration);
  }

  ngOnInit(): void {
    this.featureFlags$ = this.featureFlagService.batchGetStatus(this.partnerId, this.marketId, [
      ITEM_SELECTOR_INCLUDES_PACKAGES,
      SALES_OPPORTUNITIES_BILLING_PERIODS,
    ]);
    this.areBillingPeriodsEnabled$ = this.featureFlags$.pipe(
      map((featureFlags) => featureFlags && featureFlags[SALES_OPPORTUNITIES_BILLING_PERIODS]),
    );

    this.editing = !!this.editable && !!this.startEditing;
    this.salesOrderConfirmationService.initialize(
      this.lineItems,
      this.orderId,
      this.opportunityId,
      this.partnerId,
      this.marketId,
      this.businessId,
      this.defaultDisplayCurrency,
    );
    this.subscriptions.push(
      this.salesOrdersService
        .getConfig(this.partnerId, this.marketId)
        .pipe(map((config) => config.salespersonOptions?.disableSellingStandaloneProducts))
        .subscribe((hideSellingStandaloneProducts) => {
          this.hideSellingStandaloneProducts = hideSellingStandaloneProducts;
        }),
    );
    this.formForLineItems = this.fb.array([]);

    this.subscriptions.push(
      this.uiLineItems$
        .pipe(
          map((uiLineItems) => {
            this.displayItems = uiLineItems;
            this.resetEditingItems();
            this.cdk.detectChanges();
          }),
          tap(() => this.salesOrderConfirmationService.loading$$.next(false)),
        )
        .subscribe(),
    );

    this.subscriptions.push(this.loading$.pipe(tap((loading) => this.loading.emit(loading))).subscribe());
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.salesOrderConfirmationService.clearOrderStateCache();
  }

  resetEditingItems(): void {
    this.setEditingLineItems(JSON.parse(JSON.stringify(this.displayItems)));
    this.resetFormToEditingItems();
  }

  resetFormToEditingItems(): void {
    this.formForLineItems = this.fb.array(
      this.editingItems.map((li) => {
        const revenueControls = this.revenueControlFromLineItemRevenue(li.lineItem?.currentRevenue);
        const quantityEnabled = this.bypassQuantityRestrictions || li.isMultiActivatable;
        const nonBillable = li.lineItem?.nonBillable ?? false;
        const formGroup = this.fb.group({
          quantity: [
            { value: quantityEnabled ? li.lineItem.quantity : 1, disabled: !quantityEnabled },
            [Validators.min(1)],
          ],
          revenueComponents: this.fb.array(revenueControls),
          shouldInvoice: [!nonBillable],
        });
        return formGroup;
      }),
    );
  }

  revenueControlFromLineItemRevenue(revenue: Revenue): UntypedFormGroup[] {
    return (revenue?.revenueComponents || []).map((rc) =>
      this.fb.group({
        revenue: [(rc.value || 0) / 100, [Validators.required, this.validateRevenue]],
        period: [rc.period === undefined ? RevenuePeriod.ONETIME : rc.period, [Validators.required]],
        startingAt: [rc.isStartingRevenue],
      }),
    );
  }

  hasNoRevenue(uiLineItem: UILineItemWithEditionPricing, index: number): boolean {
    const revenueComponents = uiLineItem.lineItem?.currentRevenue?.revenueComponents;

    if ((revenueComponents || []).length === 0) {
      this.addRevenueRow(index);
    }

    return false;
  }

  validateRevenue = (c: UntypedFormControl): ValidationErrors | null => {
    const revenueValue = c.value;
    if (isNaN(revenueValue)) {
      return { pattern: true };
    }
    if (Number(revenueValue) < 0 && this.allowNegativeValues === false) {
      return { min: true };
    }
    return null;
  };

  edit(): void {
    this.editing = true;
  }

  cancel(): void {
    this.editing = false;
    this.resetEditingItems();
  }

  save(): void {
    if (!this.isFormValid()) {
      return;
    }

    const items = this.formatEditingItems();
    this.saving = true;
    this.salesOrderConfirmationService
      .updateLineItems(items)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.displayItems = JSON.parse(JSON.stringify(this.editingItems));
          this.resetEditingItems();
          this.saving = false;
          this.editing = false;
          this.alertService.openSuccessSnack('FRONTEND.SALES_UI.CONTENTS_UPDATED');
          this.cdk.detectChanges();
        },
        error: () => {
          this.saving = false;
          this.alertService.openErrorSnack('FRONTEND.SALES_UI.ERRORS.ERROR_UPDATING_CONTENTS');
          this.cdk.detectChanges();
        },
      });
  }

  formatEditingItems(): LineItem[] {
    this.writeFormValuesToEditingItems();
    return this.editingItems.map((li) => li.lineItem);
  }

  writeFormValuesToEditingItems(): void {
    this.editingItems.map((uiLineItem, index) => {
      const lineItemFormGroup = this.formForLineItems.controls[index] as UntypedFormGroup;
      uiLineItem.lineItem.quantity = lineItemFormGroup.controls.quantity.value;
      const revenueComponentsFormArray = lineItemFormGroup.controls?.revenueComponents as UntypedFormArray;
      const revenueComponents = revenueComponentsFormArray.controls.map((fa) => {
        const fg = fa as UntypedFormGroup;
        return new RevenueComponent({
          value: Math.round(fg.controls.revenue.value * 100),
          period: fg.controls.period.value,
          isStartingRevenue: fg.controls.startingAt.value,
        });
      });
      uiLineItem.lineItem.currentRevenue = new Revenue({ revenueComponents: revenueComponents });
      uiLineItem.lineItem.nonBillable = !lineItemFormGroup.controls?.shouldInvoice.value;
    });
  }

  // When using the component without a backing order, this can be used to retrieve the lineitems that are in the editor when an external
  // save button is clicked (or some other trigger)
  public getCurrentLineItems(): LineItem[] {
    return this.formatEditingItems();
  }

  public isFormValid(): boolean {
    const valid = this.formForLineItems.valid;
    if (!valid) {
      this.formForLineItems.markAsTouched();
      this.formForLineItems.updateValueAndValidity();
      this.editPriceComponents.map((c) => c.markAsTouched());
    }
    if (this.editingItems.length <= 0) {
      this.alertService.openErrorSnack('FRONTEND.SALES_UI.ERRORS.ERROR_MUST_HAVE_ITEMS');
      return false;
    }
    if (this.editingItems.find((item) => item.lineItem?.currentRevenue?.revenueComponents.length === 0)) {
      this.alertService.openErrorSnack('FRONTEND.SALES_UI.ERRORS.ERROR_MUST_HAVE_PRICING');
      return false;
    }

    return valid;
  }

  // After the component is initialized, if you would like to specify more line items you can use this function
  public addSpecifiedLineItems(lineItems: LineItem[]): void {
    this.salesOrderConfirmationService.addSpecifiedLineItems(lineItems);
  }

  onEditBillingSchedule(lineItemIndex: number): void {
    const uiLineItem = this.editingItems[lineItemIndex];
    const revenueComponent = this.formForLineItems.controls[lineItemIndex].value?.revenueComponents[0];
    const lineItem = uiLineItem.lineItem;
    const dialogRef = this.dialog.open(BillingTermDialogComponent, {
      data: {
        itemId: lineItem.appKey?.appId ?? lineItem.packageId,
        name: uiLineItem.name,
        editionName: uiLineItem.editionName,
        iconUrl: uiLineItem.iconUrl,
        revenueComponent: { revenue: revenueComponent.revenue * 100, period: revenueComponent.period },
        startDate: lineItem.billingPeriod?.startDate,
        endDate: lineItem.billingPeriod?.endDate,
      },
      maxWidth: '90vw',
      maxHeight: '90vh',
      width: '500px',
    });
    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((dialogOutput: BillingTermDialogOutput) => {
        if (!dialogOutput) {
          return;
        }

        lineItem.billingPeriod = new BillingPeriod({
          startDate: dialogOutput.startDate,
          endDate: dialogOutput.endDate,
        });
        lineItem.currentRevenue.revenueComponents[0].period = dialogOutput.billingFrequency;
        this.formForLineItems.controls[lineItemIndex].patchValue(lineItem.currentRevenue);

        if (dialogOutput.addAnotherTerm) {
          this.onAddBillingSchedule(lineItemIndex);
        }
      });
  }

  onAddBillingSchedule(lineItemIndex: number): void {
    this.writeFormValuesToEditingItems();
    const deepCopiedItem = structuredClone(this.editingItems[lineItemIndex]);
    delete deepCopiedItem.lineItem.billingPeriod;
    this.editingItems.splice(lineItemIndex + 1, 0, deepCopiedItem);
    this.setEditingLineItems(this.editingItems);
    this.resetFormToEditingItems();
    this.cdk.detectChanges();
    this.onEditBillingSchedule(lineItemIndex + 1);
  }

  addRevenueRow(index: number): void {
    const components = this.editingItems[index]?.lineItem?.currentRevenue?.revenueComponents || [];
    components.push(new RevenueComponent());
    this.formForLineItems.controls[index]['controls']?.revenueComponents.push(
      this.fb.group({
        revenue: [0, [Validators.required, this.validateRevenue]],
        period: [RevenuePeriod.ONETIME, [Validators.required]],
        startingAt: [false],
      }),
    );
    this.editingItems[index].lineItem.currentRevenue.revenueComponents = components;
  }

  deleteRevenueRow(revenueComponent: RevenueComponent, liIndex: number, rcIndex: number): void {
    this.editingItems[liIndex].lineItem?.currentRevenue?.revenueComponents.splice(rcIndex, 1);
    const lineItemFormGroup = this.formForLineItems.controls[liIndex] as UntypedFormGroup;
    (lineItemFormGroup.controls?.revenueComponents as UntypedFormArray).removeAt(rcIndex);
  }

  addLineItems(featureFlags: FeatureFlagStatusInterface): void {
    const itemSelectorIncludesPackages = featureFlags[ITEM_SELECTOR_INCLUDES_PACKAGES];
    const preselectedPackages = this.editingItems
      .filter((item) => !!item.lineItem?.packageId)
      .map((item) => new Package({ packageId: item.lineItem.packageId }));
    const preselectedApps = this.editingItems
      .filter((item) => !!item.lineItem?.appKey?.appId)
      .map((item) => {
        return new App({
          key: {
            appId: item.lineItem.appKey.appId,
            editionId: item.lineItem.appKey.editionId,
          },
          parentRequirements: {
            parentDetails: {
              key: {
                appId: item.parentAppId,
              },
              name: item.parentName,
            },
          },
          appType: item.appType,
        });
      });
    const dialogRef = this.dialog.open(SalesOrderLineItemSelectorDialogComponent, {
      data: {
        marketId: this.marketId,
        preselectedApps: this.allowDuplicateItems ? [] : preselectedApps,
        preselectedPackages: this.allowDuplicateItems ? [] : preselectedPackages,
        hideSellingStandaloneProducts: this.hideSellingStandaloneProducts,
        hidePackages: this.hidePackages,
        hideOwnerName: true,
        itemSelectorIncludesPackages: itemSelectorIncludesPackages,
      },
      width: '1000px',
      height: '900px',
      maxWidth: '95vw',
      minHeight: '420px',
      maxHeight: '85vh',
    });
    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((result) => {
        if (result) {
          this.writeFormValuesToEditingItems();
          let updatedItems = this.editingItems;
          let selectedPackages = result.packages;
          let selectedApps = result.apps;

          if (!this.allowDuplicateItems) {
            updatedItems = this.deselectItems(
              updatedItems,
              preselectedPackages,
              preselectedApps,
              selectedPackages,
              selectedApps,
            );
            if (!this.hidePackages) {
              selectedPackages = this.dedupePackages(updatedItems, selectedPackages);
            }
            selectedApps = this.dedupeApps(updatedItems, selectedApps);
          }

          if (!this.hidePackages) {
            updatedItems = updatedItems.concat(
              selectedPackages.map((pkg) => this.salesOrderConfirmationService.packageToUILineItem(pkg)),
            );
          }

          updatedItems = updatedItems.concat(
            selectedApps.map((app) => this.salesOrderConfirmationService.appWithPossibleRevenueToUILineItem(app)),
          );

          // Remove invalid revenue values from revenue components aka line items with contact sales (value == -1)
          updatedItems = updatedItems.map((item) => {
            item.lineItem.currentRevenue.revenueComponents = item.lineItem?.currentRevenue?.revenueComponents.filter(
              (revenue) => revenue.value >= 0,
            );
            return item;
          });

          // Update items in UI
          this.setEditingLineItems(updatedItems);

          this.resetFormToEditingItems();
          this.cdk.detectChanges();
        }
      });
  }

  deleteLineItem(index: number): void {
    this.editingItems.splice(index, 1);
    this.formForLineItems.removeAt(index);
  }

  // called by parent components for print-friendly display
  public openDropdown(): void {
    this.openExpansionPanel$$.next(true);
  }

  private setEditingLineItems(uiLineItems: UILineItemWithEditionPricing[]): void {
    // Sort the revenue components to ensure one time always appears last, initially
    uiLineItems?.forEach?.((uilineItem) => {
      uilineItem.lineItem?.currentRevenue?.revenueComponents?.sort((a: RevenueComponent, b: RevenueComponent) => {
        if (a.period === RevenuePeriod.ONETIME || !a.period) {
          return 1;
        }
        if (b.period === RevenuePeriod.ONETIME || !b.period) {
          return -1;
        }
        return 0;
      });
    });
    this.editingItems = uiLineItems;
  }

  private deselectItems(
    updatedItems: UILineItemWithEditionPricing[],
    preselectedPackages: Package[],
    preselectedApps: App[],
    selectedPackages: Package[],
    selectedApps: App[],
  ): UILineItemWithEditionPricing[] {
    // Find packages that have been deselected in the selector
    const deselectedPackageIds = [];
    if (!this.hidePackages) {
      preselectedPackages.map((pkg) => {
        const stillSelected = selectedPackages.some((selected) => selected.packageId === pkg.packageId);
        if (!stillSelected) {
          deselectedPackageIds.push(pkg.packageId);
        }
      });
      updatedItems = updatedItems.filter(
        (item) => !item.lineItem.packageId || !deselectedPackageIds.includes(item.lineItem.packageId),
      );
    }
    // Find apps that have been deselected in the selector
    const deselectedAppIds = [];
    preselectedApps.map((app) => {
      const stillSelected = selectedApps.some((selected) => selected.key.appId === app.key.appId);
      if (!stillSelected) {
        deselectedAppIds.push(app.key.appId);
      }
    });

    // Remove packages and apps that have been deselected in the selector
    updatedItems = updatedItems.filter(
      (item) =>
        (!!item.lineItem.packageId && !deselectedPackageIds.includes(item.lineItem.packageId)) ||
        (!!item.lineItem.appKey?.appId && !deselectedAppIds.includes(item.lineItem.appKey.appId)),
    );

    return updatedItems;
  }

  private dedupeApps(updatedItems: UILineItemWithEditionPricing[], addedApps: App[]): App[] {
    // Add only newly added apps so that preselected apps that have not been deselected do not have quantity and revenue overwritten
    return addedApps.reduce((added, app) => {
      if (!updatedItems.some((item) => item.lineItem.appKey?.appId === app.key.appId)) {
        added.push(app);
      }
      return added;
    }, []);
  }

  private dedupePackages(updatedItems: UILineItemWithEditionPricing[], addedPackages: Package[]): Package[] {
    // Add only newly added packages so that preselected packages that remain do not have quantity and revenue overwritten
    return addedPackages.reduce((added, pkg) => {
      if (this.allowDuplicateItems || !updatedItems.some((item) => item.lineItem.packageId === pkg.packageId)) {
        added.push(pkg);
      }
      return added;
    }, []);
  }
}
